import {DataTable} from '../ui/DataTable';
import {StatusBadge} from '../ui/StatusBadge';
import {formatDateOnly} from '../dates';
import {payableMoney} from './payables-format';
import {PortalSharedDecisions} from './PortalSharedDecisions';
import { AcceptSuccessorInvitation } from './ConveyancingCompletionAlpha';
import { HostedCardPayments } from './HostedCardPayments';
import { alphaSave } from '../ui/alpha-save';
import { Link } from 'react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, authenticatedFetch } from '../api';
import { ErrorState, Loading } from '../ui/State';
import { HouseholdAlpha } from './HouseholdAlpha';
import { RefundRequestsAlpha } from './RefundsAlpha';
import { InformationResponsesAlpha } from './InformationAlpha';
import { ResidentPollsAlpha } from './PollsAlpha';
import { ResidentIncidents } from './IncidentsAlpha';
type Home = { id: string; display_name: string; building_name: string };
type Statement = {
  id: string;
  reference: string;
  schedule: string;
  available: boolean;
  notice?: string;
  balance?: string;
  truncated?: boolean;
  postings?: {
    id: string;
    posting_date: string;
    posting_type: string;
    debit_amount: string;
    credit_amount: string;
  }[];
  demands?: {
    id: string;
    reference: string;
    due_date: string;
    total_amount: string;
    status: string;
  }[];
};
type Model = {
  home: Home;
  statements: Statement[];
  documents: { id: string; title: string; versionId: string }[];
  requests: { id: string; title: string; status: string; is_consent:boolean }[];
};
export function LeaseholderAlpha() {
  const [homes, setHomes] = useState<Home[]>(),
    [selected, setSelected] = useState(''),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    let active = true;
    api<{ homes: Home[] }>('/api/portal/leaseholder')
      .then((r) => {
        if (active) {
          setHomes(r.homes);
          if (r.homes.length === 1) setSelected(r.homes[0]!.id);
        }
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <>
      <h1>Your home</h1>
      <PortalSharedDecisions/>
      <AcceptSuccessorInvitation
        onAccepted={() => {
          void api<{ homes: Home[] }>('/api/portal/leaseholder')
            .then((r) => {
              setHomes(r.homes);
              if (r.homes.length === 1) setSelected(r.homes[0]!.id);
            })
            .catch(setError);
        }}
      />
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!homes ? (
        <Loading />
      ) : homes.length === 0 ? (
        <p>No verified property access is available. Ask your manager to review your access.</p>
      ) : (
        <>
          {homes.length>1&&<label className="resident-property-picker">
            Property
            <select value={selected} onChange={(e) => setSelected(e.target.value)}>
              <option value="">Choose your home</option>
              {homes.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.display_name} · {h.building_name}
                </option>
              ))}
            </select>
          </label>}
          {selected && <HomeWorkspace key={selected} id={selected} />}
        </>
      )}
    </>
  );
}
function HomeWorkspace({ id }: { id: string }) {
  const [data, setData] = useState<Model>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  const pending = useRef<object | null>(null),
    root = '/api/portal/leaseholder/' + id;
  const load = () => api<Model>(root).then(setData);
  useEffect(() => {
    let active = true;
    api<Model>(root)
      .then((r) => {
        if (active) setData(r);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [id]);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget,
      f = new FormData(form);
    pending.current ??= {
      title: f.get('title'),
      detail: f.get('detail'),
      kind: f.get('kind')==='Alterations application'?'Enquiry':f.get('kind'),
      alterationsApplication:f.get('kind')==='Alterations application',
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      await alphaSave(pending, root + '/requests', {
        method: 'POST',
        body: JSON.stringify(pending.current),
      });
      await load();
      pending.current = null;
      form.reset();
      setNotice('Your request has been saved.');
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function download(version: string) {
    try {
      const r = await authenticatedFetch(root + '/documents/' + version);
      if (!r.ok) throw Error('Document unavailable');
      const url = URL.createObjectURL(await r.blob()),
        a = document.createElement('a');
      a.href = url;
      a.download = 'shared-document';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!data ? (
        <Loading />
      ) : (
        <>
          <section className="panel portal-relationship"><h2>Your property</h2>
          <p><strong>{data.home.building_name} · {data.home.display_name}</strong></p>
          <p className="workspace-source-note">Only the property and records explicitly shared with your account appear here.</p></section>
          <nav className="portal-local-sections" aria-label="Selected home sections"><a href="#home-requests">Requests</a><a href="#home-accounts">Accounts</a><a href="#home-documents">Documents</a></nav>
          <section className="panel resident-requests">
            <h2 id="home-requests" tabIndex={-1}>Your requests</h2>
            {!data.requests.length?<p>No requests are recorded for this property.</p>:<DataTable
              caption={<span className="sr-only">Requests shared with this property</span>} scrollLabel="Your home requests"
              rows={data.requests} pageSize={18} getRowKey={r=>r.id}
              rowHeader={{id:'request',header:'Request',render:r=><Link to={(r.is_consent?'/portal/consents?case=':'/portal/requests/')+r.id}>{r.title}</Link>}}
              columns={[{id:'state',header:'State',render:r=><StatusBadge text={r.status}/>}]} />}
            {notice && <p role="status">{notice}</p>}
            <details className="resident-request-composer"><summary>New request</summary>
            <form className="form" onSubmit={submit}>
              <fieldset disabled={busy || !!pending.current}>
                <legend>Send a request</legend>
                <label>
                  Type
                  <select name="kind" aria-label="Type">
                    <option>Enquiry</option>
                    <option>Repair</option><option>Alterations application</option>
                    <option>Complaint</option><option>Information request</option>
                  </select>
                </label>
                <label>
                  Subject
                  <input name="title" required minLength={3} maxLength={200} />
                </label>
                <label>
                  Details
                  <textarea name="detail" required minLength={10} maxLength={5000} />
                </label>
              </fieldset>
              <p>For an immediate danger, use your building’s emergency contact arrangements.</p>
              <button disabled={busy}>
                {busy ? 'Saving…' : pending.current ? 'Retry the same request' : 'Save request'}
              </button>
            </form></details>
          </section>
          <section className="panel resident-accounts">
            <h2 id="home-accounts" tabIndex={-1}>Your accounts</h2>
            {!data.statements.length && <p>No account has been shared yet.</p>}
            {data.statements.map((s) => (
              <article key={s.id}>
                <h3>
                  {s.schedule} · {s.reference}
                </h3>
                {!s.available ? (
                  <p>{s.notice}</p>
                ) : (
                  <>
                    <p className="resident-account-balance">Account balance: <strong>{payableMoney(s.balance)}</strong></p>
                    <Link to={'/portal/payment-plans/' + s.id}>Payment arrangements</Link>
                    <p>Positive balances are amounts outstanding; negative balances are credit.</p>
                    <details className="resident-account-detail"><summary>Demands and transactions</summary>
                    <h4>Demands</h4>
                    {!s.demands?.length?<p>No demands are included in this shared statement.</p>:<DataTable
                      caption={<span className="sr-only">Demands in this shared statement</span>} scrollLabel={`Demands for ${s.reference}`}
                      rows={s.demands} pageSize={18} getRowKey={d=>d.id}
                      rowHeader={{id:'reference',header:'Reference',render:d=>d.reference}}
                      columns={[{id:'amount',header:'Amount',render:d=>payableMoney(d.total_amount)},
                        {id:'due',header:'Due',render:d=>formatDateOnly(d.due_date,'Not recorded')},
                        {id:'state',header:'State',render:d=><StatusBadge text={d.status}/>}]} />}
                    <h4>Transactions</h4>
                    {s.truncated&&<p>Showing the latest 200 transactions. Ask your manager for a complete statement.</p>}
                    {!s.postings?.length?<p>No transactions are included in this shared statement.</p>:<DataTable
                      caption={<span className="sr-only">Transactions in this shared statement</span>} scrollLabel={`Transactions for ${s.reference}`}
                      rows={s.postings} pageSize={18} getRowKey={p=>p.id}
                      rowHeader={{id:'date',header:'Date',render:p=>formatDateOnly(p.posting_date,'Not recorded')}}
                      columns={[{id:'type',header:'Type',render:p=>p.posting_type},
                        {id:'debit',header:'Debit',render:p=>payableMoney(p.debit_amount)},
                        {id:'credit',header:'Credit',render:p=>payableMoney(p.credit_amount)}]} />}
                    </details>
                  </>
                )}
              </article>
            ))}
          </section>
          <details className="resident-card-payments"><summary>Card payments</summary><HostedCardPayments
            premises={id}
            demands={data.statements.flatMap((s) => (s.available ? (s.demands ?? []) : []))}
          /></details>
          <section className="panel resident-documents">
            <h2 id="home-documents" tabIndex={-1}>Shared documents</h2>
            {data.documents.length ? (
              <DataTable caption={<span className="sr-only">Documents explicitly shared with this property</span>}
                scrollLabel="Shared home documents" rows={data.documents} pageSize={18} getRowKey={d=>d.id}
                rowHeader={{id:'document',header:'Document',render:d=><button className="register-record-link" onClick={()=>void download(d.versionId)}>{d.title}</button>}} columns={[]}/>
            ) : (
              <p>No documents are currently shared.</p>
            )}
          </section>
          <details className="portal-household-services"><summary>Household, incidents and other resident services</summary>
          <HouseholdAlpha premises={id} />
          <ResidentIncidents premises={id} />
          <ResidentPollsAlpha premises={id} />
          <InformationResponsesAlpha premises={id} />
          <RefundRequestsAlpha premises={id} accounts={data.statements} />
          </details>
        </>
      )}
    </>
  );
}
